import React, { useState } from 'react';
import { Play, Flag, Calculator } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer/VideoPlayer';
import { useSpeedAnalysisStore } from '../stores/speedAnalysisStore';
import { useVideoStore } from '../stores/videoStore';
import { cn } from '../utils/cn';
import BackToDashboard from '../components/BackToDashboard';

const SpeedPage: React.FC = () => {
  const {
    startFrame,
    endFrame,
    distance,
    fps,
    setStartFrame,
    setEndFrame,
    setDistance,
    setFps,
    reset,
    calculateResults,
  } = useSpeedAnalysisStore();

  const { currentFrame } = useVideoStore();
  const [showResults, setShowResults] = useState(false);
  const [customFps, setCustomFps] = useState<string>(fps.toString());
  const [isCustomFps, setIsCustomFps] = useState(false);

  const handleStartFrame = () => {
    setStartFrame(currentFrame);
  };

  const handleEndFrame = () => {
    setEndFrame(currentFrame);
  };

  const handleAnalyze = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    reset();
    setShowResults(false);
  };

  const handleFpsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setIsCustomFps(true);
      // Keep the current fps value when switching to custom
      setCustomFps(fps.toString());
    } else {
      setIsCustomFps(false);
      setFps(Number(selectedValue));
    }
  };

  const handleCustomFpsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomFps(value);
    
    // Update the actual fps if the value is valid
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue > 0) {
      setFps(numValue);
    }
  };

  const results = showResults ? calculateResults() : null;
  const canAnalyze = startFrame !== null && endFrame !== null && distance > 0;
  
  // フレーム数の計算（現在のfpsに基づいて）
  const frameCount = startFrame !== null && endFrame !== null 
    ? Math.abs(endFrame - startFrame) / 4 // 4で割って補正
    : null;

  // 100m予測タイムの計算
  const predicted100mTime = results?.speed ? (100 / results.speed).toFixed(2) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <h1 className="text-3xl font-bold text-gray-900">スピード分析</h1>
      
      <div className="mt-6 space-y-6">
        <VideoPlayer />
        
        <div className="bg-white p-6 rounded-lg shadow space-y-6">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleStartFrame}
              className={cn(
                "btn btn-primary space-x-2",
                startFrame !== null && "bg-green-600 hover:bg-green-700"
              )}
            >
              <Play className="w-4 h-4" />
              <span>開始フレーム {startFrame !== null ? `(${startFrame})` : ''}</span>
            </button>
            
            <button
              onClick={handleEndFrame}
              className={cn(
                "btn btn-primary space-x-2",
                endFrame !== null && "bg-red-600 hover:bg-red-700"
              )}
            >
              <Flag className="w-4 h-4" />
              <span>終了フレーム {endFrame !== null ? `(${endFrame})` : ''}</span>
            </button>

            <button
              onClick={handleReset}
              className="btn btn-secondary"
            >
              リセット
            </button>
          </div>

          {/* フレーム数表示エリア */}
          {frameCount !== null && (
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-lg font-semibold text-gray-900">
                フレーム数: <span className="text-2xl text-primary-600">
                  {fps >= 120 ? Math.floor(frameCount / 2) : frameCount}
                </span>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                現在のフレームレート: {fps} fps
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                距離 (m)
              </label>
              <input
                type="number"
                min="0"
                value={distance}
                onChange={(e) => setDistance(Number(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                フレームレート (fps)
              </label>
              <div className="mt-1 flex space-x-2">
                <select
                  value={isCustomFps ? 'custom' : fps.toString()}
                  onChange={handleFpsChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="60">60 fps</option>
                  <option value="120">120 fps</option>
                  <option value="240">240 fps</option>
                  <option value="custom">手入力</option>
                </select>
                
                {isCustomFps && (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={customFps}
                    onChange={handleCustomFpsChange}
                    placeholder="FPS値を入力"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              className={cn(
                "btn btn-primary px-8 py-3 text-lg",
                !canAnalyze && "opacity-50 cursor-not-allowed"
              )}
            >
              <Calculator className="w-5 h-5 mr-2" />
              分析開始
            </button>
          </div>

          {results && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">分析結果</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">フレーム数</div>
                  <div className="text-2xl font-bold text-gray-900">{results.frameCount}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">タイム (秒)</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {results.time.toFixed(3)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">スピード (m/s)</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {results.speed.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">100m予測タイム (秒)</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {predicted100mTime}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
            <h4 className="font-medium mb-2">操作手順</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>測定範囲を「開始」と「終了」ボタンをタップして決定</li>
              <li>距離を入力</li>
              <li>フレームレートを選択 (60/120/240 fps) または手入力</li>
              <li>「分析開始」ボタンをクリック</li>
              <li>計算された分析結果を確認</li>
            </ol>
            <p className="mt-2 text-xs">※データがおかしい場合はフレームレートを60に戻し、最初からやり直してください。</p>
          </div>
        </div>
      </div>
      <BackToDashboard />
    </div>
  );
};

export default SpeedPage;